/**
 * 文本尺寸测量（与 canvasRenderer 换行逻辑一致，用于宽度/高度自适应与最小尺寸约束）
 * 使用离屏 Canvas 2D measureText，不依赖 DOM。
 */

import type { ElementPatch, TextElement } from '@/components/canvas/document/publicTypes';
import { fixCjkLineStartsPullBack } from './cjkLineStart';
import { getPlainTextForLayout } from './textContent';
import { getTextLayoutFromElement } from '../model/textStyles';
import { firstStroke } from '@/components/canvas/elements/model';

let measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureCtx(): CanvasRenderingContext2D {
  if (measureCtx) return measureCtx;
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!canvas) throw new Error('textMeasure requires DOM');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  measureCtx = ctx;
  return ctx;
}

function measureTextWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  font: string,
  letterSpacing?: number
): number {
  ctx.font = font;
  if (letterSpacing == null || letterSpacing === 0) return ctx.measureText(text).width;
  let w = 0;
  for (const char of text) w += ctx.measureText(char).width + letterSpacing;
  return w;
}

/**
 * 将文本按 maxContentWidth 换行，返回每行文本与每行宽度（不含 letterSpacing 尾随）。
 * 与 canvasRenderer.drawText 共用，保证「自动增高」与导出换行一致。
 */
export function wrapCanvasPlainText(
  ctx: CanvasRenderingContext2D,
  text: string,
  font: string,
  maxContentWidth: number,
  letterSpacing?: number
): { lines: string[]; lineWidths: number[] } {
  ctx.font = font;
  const lines: string[] = [];
  const lineWidths: number[] = [];
  const paragraphs = text.split('\n');

  for (let p = 0; p < paragraphs.length; p++) {
    const para = paragraphs[p];
    if (para.length === 0) {
      lines.push('');
      lineWidths.push(0);
      continue;
    }
    let currentLine = '';
    let currentW = 0;
    for (const char of para) {
      const charW = ctx.measureText(char).width + (letterSpacing ?? 0);
      if (currentW + charW > maxContentWidth && currentLine.length > 0) {
        lines.push(currentLine);
        lineWidths.push(measureTextWidth(ctx, currentLine, font, letterSpacing));
        currentLine = char;
        currentW = ctx.measureText(char).width + (letterSpacing ?? 0);
      } else {
        currentLine += char;
        currentW += charW;
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
      lineWidths.push(measureTextWidth(ctx, currentLine, font, letterSpacing));
    }
  }

  if (lines.length === 0) {
    lines.push('');
    lineWidths.push(0);
  }
  const fixedLines = fixCjkLineStartsPullBack(lines);
  const fixedWidths = fixedLines.map((line) => measureTextWidth(ctx, line, font, letterSpacing));
  return { lines: fixedLines, lineWidths: fixedWidths };
}

/**
 * 将 TextElement.lineHeight 转为行高（px），与 DOM/CSS 及 canvasRenderer.drawText 一致。
 * - 缺省：按 1.2 倍字号（与 TextElement 展示默认一致）
 * - number：无单位倍数 × fontSize
 * - string：纯小数（如 "1.5"）视为倍数；≥10 视为绝对 px（如 "24" / "24px" 前缀）
 */
/** 与浏览器 subpixel 行盒对齐：略加余量，避免导出累计行高偏矮 */
const LINE_HEIGHT_SLACK_PX = 0.35;

export function parseTextLineHeightToPx(
  lineHeight: number | string | undefined,
  fontSize: number
): number {
  let v: number;
  if (lineHeight == null) v = fontSize * 1.2;
  else if (typeof lineHeight === 'number') v = fontSize * lineHeight;
  else {
    const parsed = parseFloat(String(lineHeight));
    v = !Number.isFinite(parsed) ? fontSize * 1.2 : parsed >= 10 ? parsed : fontSize * parsed;
  }
  return v + LINE_HEIGHT_SLACK_PX;
}

export interface TextBlockSizeOptions {
  fontFamily: string;
  fontSize: number;
  fontWeight: number | string;
  fontStyle: string;
  letterSpacing?: number;
  lineHeight?: number | string;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  /** 内容区最大宽度（不含 padding），超过则换行 */
  maxContentWidth: number;
}

export interface TextBlockSizeResult {
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  lineCount: number;
}

export interface VerticalTextBlockSizeOptions {
  fontFamily: string;
  fontSize: number;
  fontWeight: number | string;
  fontStyle: string;
  lineHeight?: number | string;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  /** 内容区最大高度（不含 padding），超过则向左换列 */
  maxContentHeight: number;
}

export interface VerticalTextBlockSizeResult {
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  columnCount: number;
}

/**
 * 计算文本块在「宽度自适应、最大宽度限制下」的尺寸及最小尺寸约束。
 * - 若单行未超 maxContentWidth：width = padding + 单行宽，height = padding + 1 行高。
 * - 若超过则按 maxContentWidth 换行：width = padding + maxContentWidth，height = padding + 行数×行高。
 * - minWidth/minHeight：当前内容所需的最小宽高（用于拖拽缩放时不得小于此值）。
 */
export function computeTextBlockSize(
  text: string,
  options: TextBlockSizeOptions
): TextBlockSizeResult {
  const ctx = getMeasureCtx();
  const {
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    letterSpacing,
    lineHeight,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    maxContentWidth,
  } = options;

  const font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
  const lh = parseTextLineHeightToPx(lineHeight, fontSize);
  const pt = paddingTop;
  const pr = paddingRight;
  const pb = paddingBottom;
  const pl = paddingLeft;

  const singleLineW = measureTextWidth(ctx, text.replace(/\n/g, ' '), font, letterSpacing);
  const innerMax = Math.max(1, maxContentWidth);

  if (singleLineW <= innerMax && !text.includes('\n')) {
    const w = pl + pr + singleLineW;
    const h = pt + pb + lh;
    return {
      width: Math.ceil(w),
      height: Math.ceil(h),
      minWidth: Math.ceil(w),
      minHeight: Math.ceil(h),
      lineCount: 1,
    };
  }

  const { lines, lineWidths } = wrapCanvasPlainText(ctx, text, font, innerMax, letterSpacing);
  const lineCount = lines.length;
  const maxLineW = lineWidths.length > 0 ? Math.max(...lineWidths) : 0;
  const contentW = Math.min(Math.max(singleLineW, maxLineW), innerMax);
  const width = pl + pr + contentW;
  const height = pt + pb + lineCount * lh;
  const minWidth = pl + pr + (lineWidths.length > 0 ? Math.max(...lineWidths) : 0);
  const minHeight = pt + pb + lineCount * lh;

  return {
    width: Math.ceil(width),
    height: Math.ceil(height),
    minWidth: Math.ceil(minWidth),
    minHeight: Math.ceil(minHeight),
    lineCount,
  };
}

/**
 * 竖排文本尺寸：以内容高度作为列高限制，内容超出时向左增加列宽。
 * CSS vertical-rl 中 `line-height` 更接近列间水平步进，因此这里用它估算列宽。
 */
export function computeVerticalTextBlockSize(
  text: string,
  options: VerticalTextBlockSizeOptions,
): VerticalTextBlockSizeResult {
  const {
    fontSize,
    lineHeight,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    maxContentHeight,
  } = options;

  const lh = parseTextLineHeightToPx(lineHeight, fontSize);
  const pt = paddingTop;
  const pr = paddingRight;
  const pb = paddingBottom;
  const pl = paddingLeft;
  const innerMaxH = Math.max(1, maxContentHeight);
  const maxCharsPerColumn = Math.max(1, Math.floor(innerMaxH / lh));
  const paragraphs = text.split('\n');
  let columnCount = 0;

  for (const paragraph of paragraphs) {
    columnCount += Math.max(1, Math.ceil([...paragraph].length / maxCharsPerColumn));
  }

  if (columnCount === 0) columnCount = 1;

  const contentW = columnCount * lh;
  const contentMinH = lh;
  return {
    width: Math.ceil(pl + pr + contentW),
    height: Math.ceil(pt + pb + Math.max(contentMinH, innerMaxH)),
    minWidth: Math.ceil(pl + pr + contentW),
    minHeight: Math.ceil(pt + pb + contentMinH),
    columnCount,
  };
}

/**
 * 容不下文字时的扩展尺寸（仅扩展，不缩小）。
 * 横排按当前宽度换行后扩高；竖排按当前高度换列后扩宽。
 *
 * 注意：此函数使用离屏 Canvas 字体测量，精度受字体渲染差异影响。
 * 编辑器内部应优先使用基于 DOM scrollHeight/scrollWidth 的测量（syncHeightToContentDom）。
 */
export function getTextExpandSize(
  el: TextElement,
  patch: ElementPatch<'text'> = {},
  options: { preserveTrailingEmptyBlocks?: boolean } = {},
): { width?: number; height?: number } {
  const merged: TextElement = {
    ...el,
    size: patch.size ? { ...el.size, ...patch.size } : el.size,
    appearance: patch.appearance ? { ...el.appearance, ...patch.appearance } : el.appearance,
    typography: patch.typography ? { ...el.typography, ...patch.typography } : el.typography,
    paragraph: patch.paragraph ? { ...el.paragraph, ...patch.paragraph } : el.paragraph,
    data: patch.data ? { ...el.data, ...patch.data } : el.data,
  };
  const text = getPlainTextForLayout(merged, options);
  const { typography } = getTextLayoutFromElement(merged);
  const padding = merged.size.padding;
  const pt = padding?.top ?? 0;
  const pr = padding?.right ?? 0;
  const pb = padding?.bottom ?? 0;
  const pl = padding?.left ?? 0;
  const bw = firstStroke(merged.appearance)?.width ?? 0;
  const innerW = Math.max(1, merged.size.width - pl - pr - 2 * bw);
  const innerH = Math.max(1, merged.size.height - pt - pb - 2 * bw);

  if (getTextLayoutFromElement(merged).writingMode === 'vertical-rl') {
    const wrapped = computeVerticalTextBlockSize(text, {
      fontFamily: typography.fontFamily,
      fontSize: typography.fontSize,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle ?? 'normal',
      lineHeight: typography.lineHeight,
      paddingTop: pt,
      paddingRight: pr,
      paddingBottom: pb,
      paddingLeft: pl,
      maxContentHeight: innerH,
    });
    const wantW = wrapped.width > merged.size.width ? wrapped.width : undefined;
    if (wantW !== undefined) return { width: wantW };
    return {};
  }

  const wrapped = computeTextBlockSize(text, {
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle ?? 'normal',
    letterSpacing: typography.letterSpacing,
    lineHeight: typography.lineHeight,
    paddingTop: pt,
    paddingRight: pr,
    paddingBottom: pb,
    paddingLeft: pl,
    maxContentWidth: innerW,
  });

  const wantH = wrapped.height > merged.size.height ? wrapped.height : undefined;
  if (wantH !== undefined) return { height: wantH };
  return {};
}

/**
 * 文本元素在给定宽度下的最小尺寸（用于拖拽缩放时不得小于此值）。
 * 在当前元素宽度下换行，取各行最大宽度与总行高。
 */
export function getTextMinSize(
  el: TextElement,
  currentWidth = el.size.width,
  currentHeight = el.size.height,
): { minWidth: number; minHeight: number } {
  const text = getPlainTextForLayout(el);
  const padding = el.size.padding;
  const pt = padding?.top ?? 8;
  const pr = padding?.right ?? 8;
  const pb = padding?.bottom ?? 8;
  const pl = padding?.left ?? 8;
  const bw = firstStroke(el.appearance)?.width ?? 0;
  const innerW = Math.max(1, currentWidth - pl - pr - 2 * bw);
  const innerH = Math.max(1, currentHeight - pt - pb - 2 * bw);
  const { typography } = getTextLayoutFromElement(el);

  if (getTextLayoutFromElement(el).writingMode === 'vertical-rl') {
    const result = computeVerticalTextBlockSize(text, {
      fontFamily: typography.fontFamily,
      fontSize: typography.fontSize,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle ?? 'normal',
      lineHeight: typography.lineHeight,
      paddingTop: pt,
      paddingRight: pr,
      paddingBottom: pb,
      paddingLeft: pl,
      maxContentHeight: innerH,
    });
    return { minWidth: result.minWidth, minHeight: result.minHeight };
  }

  const result = computeTextBlockSize(text, {
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle ?? 'normal',
    letterSpacing: typography.letterSpacing,
    lineHeight: typography.lineHeight,
    paddingTop: pt,
    paddingRight: pr,
    paddingBottom: pb,
    paddingLeft: pl,
    maxContentWidth: innerW,
  });

  return { minWidth: result.minWidth, minHeight: result.minHeight };
}
